import { ref, onMounted, watch } from 'vue';
import { spring } from 'motion';
import { animate } from 'motion/mini';

/**
 * Spring animation configuration
 */
const SPRING_CONFIG = {
  bounce: 0.35,
  duration: 0.6,
};

/**
 * Animate a single element with enter animation
 * Can be used directly from Options API mounted() hook
 *
 * @param {HTMLElement} element - The DOM element to animate
 * @param {number} delay - Delay before animation starts (ms)
 */
export function animateEnter(element, delay = 0) {
  if (!element) return;

  // Set initial state
  element.style.opacity = '0';
  element.style.transform = 'translateY(20px)';

  setTimeout(() => {
    animate(
      element,
      { opacity: 1, transform: 'translateY(0)' },
      {
        type: spring,
        bounce: SPRING_CONFIG.bounce,
        duration: SPRING_CONFIG.duration,
      }
    );
  }, delay);
}

/**
 * Composable for enter animation with spring physics
 * Used for staggered mount animations in widget V2 components
 *
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay before animation starts (ms)
 * @param {import('vue').Ref<boolean>} options.triggerRef - Optional ref to trigger animation manually
 * @returns {Object} - { elementRef, hasAnimated }
 */
export function useEnterAnimation(options = {}) {
  const { delay = 0, triggerRef = null } = options;

  const elementRef = ref(null);
  const hasAnimated = ref(false);

  const runAnimation = () => {
    const element = elementRef.value;
    if (!element || hasAnimated.value) return;

    animateEnter(element, delay);
    hasAnimated.value = true;
  };

  // If triggerRef is provided, watch for trigger
  // Otherwise, run animation on mount
  if (triggerRef) {
    watch(
      triggerRef,
      val => {
        if (val) runAnimation();
      },
      { immediate: true }
    );
  } else {
    onMounted(runAnimation);
  }

  return { elementRef, hasAnimated };
}
